import type { MapLocation } from '@frontend/api-types';

export type PlaceDetails = {
  address?: string;
  name: string;
  openNow?: boolean;
  phoneNumber?: string;
  photoUrls: string[];
  priceLevel?: number;
  rating?: number;
  totalRatings?: number;
  website?: string;
};

export type PlaceIconInfo = {
  bgColor: string;
  iconMaskUrl: string;
};

export type PlaceResultWithIcons = google.maps.places.PlaceResult & {
  icon_background_color?: string;
  icon_mask_base_uri?: string;
};

const PLACE_DETAIL_FIELDS = [
  'name',
  'formatted_address',
  'formatted_phone_number',
  'rating',
  'user_ratings_total',
  'price_level',
  'opening_hours',
  'website',
  'photos',
];

export const fetchPlaceDetails = (map: google.maps.Map, location: MapLocation): Promise<PlaceDetails | null> => {
  const service = new google.maps.places.PlacesService(map);

  return new Promise((resolve) => {
    service.getDetails({ fields: PLACE_DETAIL_FIELDS, placeId: location.placeId }, (place, status) => {
      if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
        resolve(null);
        return;
      }
      resolve({
        address: place.formatted_address,
        name: place.name ?? location.label,
        openNow: place.opening_hours?.isOpen(),
        phoneNumber: place.formatted_phone_number,
        photoUrls: (place.photos ?? []).slice(0, 6).map((p) => p.getUrl({ maxHeight: 400, maxWidth: 600 })),
        priceLevel: place.price_level,
        rating: place.rating,
        totalRatings: place.user_ratings_total,
        website: place.website,
      });
    });
  });
};

// Cache in-flight promises to deduplicate concurrent requests (e.g. compact map + expanded modal)
const MAX_ICON_CACHE_SIZE = 100;
const iconCache = new Map<string, Promise<PlaceIconInfo | null>>();

export const fetchPlaceIcon = (
  service: google.maps.places.PlacesService,
  placeId: string,
): Promise<PlaceIconInfo | null> => {
  const cached = iconCache.get(placeId);
  if (cached) return cached;

  const promise = new Promise<PlaceIconInfo | null>((resolve) => {
    service.getDetails({ fields: ['icon_mask_base_uri', 'icon_background_color'], placeId }, (place, status) => {
      if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
        // Transient failure — remove from cache so it can be retried
        iconCache.delete(placeId);
        resolve(null);
        return;
      }
      const { icon_background_color: bgColor, icon_mask_base_uri: iconBase } = place as PlaceResultWithIcons;
      if (!iconBase) {
        // Place genuinely has no icon — keep cached to avoid repeated lookups
        resolve(null);
        return;
      }
      resolve({ bgColor: bgColor ?? '#666', iconMaskUrl: `${iconBase}.svg` });
    });
  });

  iconCache.set(placeId, promise);

  // Evict oldest entry when cache exceeds max size
  if (iconCache.size > MAX_ICON_CACHE_SIZE) {
    const oldestKey = iconCache.keys().next().value;
    if (oldestKey) iconCache.delete(oldestKey);
  }

  return promise;
};
